import { useEffect, useState } from 'react';

const STORAGE_KEY = 'practiceDemoMode';
const EVENT_NAME = 'practiceDemoModeChange';

export const DEMO_BLUR_ATTR = 'data-demo-blur';
const BODY_CLASS = 'demo-mode';

// Mirrors demo mode onto <body> so ONE global CSS rule (App.css:
// `body.demo-mode [data-demo-blur]`) blurs every tagged PII element without a hook per cell.
const syncBodyClass = (value) => {
    try {
        if (typeof document !== 'undefined' && document.body) {
            document.body.classList.toggle(BODY_CLASS, Boolean(value));
        }
    } catch { /* ignore */ }
};

const readStored = () => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; }
};

export default function useDemoMode() {
    const [demoMode, setDemoModeState] = useState(readStored);

    useEffect(() => {
        syncBodyClass(demoMode);
    }, [demoMode]);

    useEffect(() => {
        const handler = () => setDemoModeState(readStored());
        window.addEventListener(EVENT_NAME, handler);
        window.addEventListener('storage', handler);
        return () => {
            window.removeEventListener(EVENT_NAME, handler);
            window.removeEventListener('storage', handler);
        };
    }, []);

    const setDemoMode = (next) => {
        const value = typeof next === 'function' ? next(readStored()) : Boolean(next);
        try { localStorage.setItem(STORAGE_KEY, String(value)); } catch { /* ignore */ }
        syncBodyClass(value);
        window.dispatchEvent(new CustomEvent(EVENT_NAME));
        setDemoModeState(value);
    };

    return [demoMode, setDemoMode];
}

// Returns a function that masks practice names when demo mode is on.
// Numbered (Practice 1, Practice 2, ...) so comparison tables stay readable.
// Records whose practice isn't in the provided `practices` list (deleted/historical)
// are masked as "Practice #<id>" so two unknown rows never collide as the same label.
// (practice, fallbackId) => string. fallbackId is used when `practice` is absent
// (e.g. deleted/historical records) so unknown rows still get a stable label.
export function buildPracticeNameMasker(practices, demoMode) {
    const indexById = new Map();
    (practices || []).forEach((p, i) => {
        if (p?.id != null) indexById.set(String(p.id), i + 1);
    });
    return (practice, fallbackId) => {
        const id = practice?.id ?? fallbackId;
        if (demoMode) {
            if (id == null) return 'Practice';
            const n = indexById.get(String(id));
            return n ? `Practice ${n}` : `Practice #${id}`;
        }
        return practice?.name || (id != null ? `Practice ${id}` : '');
    };
}
